import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import Dialog from '@material-ui/core/Dialog';
import Icon from '@material-ui/core/Icon';
import InputBase from '@material-ui/core/InputBase';
import LinearProgress from '@material-ui/core/LinearProgress';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import SearchIcon from '@material-ui/icons/Search';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import { withTranslation, WithTranslation } from 'react-i18next';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { useAsync } from 'react-async-hook';
import useConstant from 'use-constant';
// Modules
import { selectors as homeSelectors } from '../../redux/reducers/home';
// Ressources
import { getHistory } from '../../utils/historyUtils';
import RecentUtils from '../../utils/recentUtils';
import WindowUtils from '../../utils/windowUtils';
import { Favorite } from '../../utils/userDataUtils';
import { clsx } from '../../utils/htmlUtils';
import defaultTheme from '../../assets/styles/theme';
import {
    compactPhone,
    formatPrefix,
    getAvailableSources,
    getNavigationEntries,
    getResultRows,
    matchNavigation,
    NAVIGATION_RESULTS,
    PaletteResult,
    parseQuery,
    PaletteSource,
    QueryKind,
    RESULTS_PER_SOURCE,
} from './palette-sources';
import styles from './command-palette-style';

const SEARCH_DEBOUNCE = 350;
const QUICK_ACCESS_RESULTS = 5;

interface PaletteGroup {
    key: string;
    label: string;
    icon: string;
    color?: string;
    results: PaletteResult[];
}

interface CommandPaletteProps extends WithTranslation, WithStyles {
    open: boolean;
    onClose: () => void;
    favorites: Favorite[];
}

/** Modifier key label to display in shortcuts, Cmd on Apple hardware, Ctrl elsewhere. */
export const getModifierLabel = (): string => (/Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl');

/**
 * Opens the palette on Cmd/Ctrl+K, from anywhere in the application.
 * Exported so the page skeleton owns the open state while the shortcut stays here.
 */
export const useCommandPaletteShortcut = (onOpen: () => void): void => {
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent): void => {
            if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key?.toLowerCase() === 'k') {
                e.preventDefault();
                onOpen();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onOpen]);
};

const CommandPalette = (props: CommandPaletteProps): JSX.Element => {
    const { t, classes, open, onClose, favorites } = props;

    const [query, setQuery] = useState<string>('');
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const itemRefs = useRef<HTMLElement[]>([]);

    const navigationEntries = useMemo(() => getNavigationEntries(t), [t]);
    const availableSources = useMemo(() => getAvailableSources(navigationEntries), [navigationEntries]);

    //=======================================//
    //=============== Search ================//
    //=======================================//

    const searchEntities = useCallback(
        async (rawQuery: string, isOpen: boolean): Promise<PaletteGroup[]> => {
            const { term, kind, sourceId, navigationOnly } = parseQuery(rawQuery);
            if (!isOpen || navigationOnly) {
                return [];
            }

            const sources: PaletteSource[] = availableSources.filter(
                (source) =>
                    (sourceId ? source.id === sourceId : source.kinds.includes(kind)) &&
                    term.length >= source.minLength,
            );
            if (sources.length === 0) {
                return [];
            }

            // A phone is searched as a single run of digits, whatever the way it was typed
            const searchTerm = kind === QueryKind.Phone ? compactPhone(term) : term;

            const groups = await Promise.all(
                sources.map(async (source) => {
                    let results: PaletteResult[] = [];
                    try {
                        const response = await source.search(searchTerm, kind);
                        results = getResultRows(response)
                            .slice(0, RESULTS_PER_SOURCE)
                            .map((row) => source.format(row, t));
                    } catch (e) {
                        // One source refusing the query (rights, outage...) must not hide the others
                        results = [];
                    }
                    return {
                        key: source.id,
                        label: t(source.labelKey),
                        icon: source.icon,
                        color: source.color,
                        results,
                    };
                }),
            );

            return groups.filter((group) => group.results.length > 0);
        },
        [availableSources, t],
    );

    // The debounced wrapper is created once, so it reads the search through a ref
    const searchRef = useRef(searchEntities);
    searchRef.current = searchEntities;
    const debouncedSearch = useConstant(() =>
        AwesomeDebouncePromise(
            (rawQuery: string, isOpen: boolean) => searchRef.current(rawQuery, isOpen),
            SEARCH_DEBOUNCE,
        ),
    );
    const search = useAsync(debouncedSearch, [query, open]);

    //=======================================//
    //=============== Groups ================//
    //=======================================//

    const parsed = parseQuery(query);

    const navigationGroup: PaletteGroup = useMemo(() => {
        if (parsed.sourceId) {
            return null;
        }
        const entries = matchNavigation(navigationEntries, parsed.term, NAVIGATION_RESULTS);
        if (entries.length === 0) {
            return null;
        }
        return {
            key: 'navigation',
            label: t('navigation'),
            icon: 'menu',
            results: entries.map((entry) => ({
                key: `nav-${entry.to}`,
                title: entry.label,
                subtitle: entry.group,
                to: entry.to,
                icon: entry.icon,
            })),
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigationEntries, parsed.term, parsed.sourceId, t]);

    // With an empty input the palette offers what the user already works with
    const quickAccessGroups: PaletteGroup[] = useMemo(() => {
        if (parsed.term || parsed.sourceId) {
            return [];
        }
        const groups: PaletteGroup[] = [];

        const recents = RecentUtils.getRecentPages().slice(0, QUICK_ACCESS_RESULTS);
        if (recents.length > 0) {
            groups.push({
                key: 'recents',
                label: t('recents'),
                icon: 'history',
                color: defaultTheme.palette.colors.greyDark,
                results: recents.map((page) => ({
                    key: `recent-${page.to}`,
                    title: page.title,
                    subtitle: page.subtitle,
                    to: page.to,
                    icon: 'history',
                })),
            });
        }

        if (favorites?.length > 0) {
            groups.push({
                key: 'favorites',
                label: t('favorites'),
                icon: 'favorite',
                color: defaultTheme.palette.colors.red,
                results: favorites.slice(0, QUICK_ACCESS_RESULTS).map((favorite) => ({
                    key: `favorite-${favorite.to}`,
                    title:
                        favorite.name ||
                        navigationEntries.find((entry) => favorite.to.startsWith(entry.to))?.label ||
                        favorite.to,
                    subtitle: favorite.to,
                    to: favorite.to,
                    icon: 'favorite',
                })),
            });
        }

        return groups;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [favorites, navigationEntries, parsed.term, parsed.sourceId, t]);

    const groups: PaletteGroup[] = [
        ...quickAccessGroups,
        ...(navigationGroup ? [navigationGroup] : []),
        ...(search.result || []),
    ];
    const flatResults: PaletteResult[] = groups.reduce((acc, group) => [...acc, ...group.results], []);

    useEffect(() => {
        setActiveIndex(0);
    }, [query, search.result]);

    useEffect(() => {
        itemRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
    }, [activeIndex]);

    //========================================//
    //=============== Actions ================//
    //========================================//

    const handleSelect = (result: PaletteResult, newTab: boolean) => (): void => {
        if (!result) {
            return;
        }
        if (newTab) {
            WindowUtils.openNewTab(result.to);
            return;
        }
        onClose();
        getHistory().push(result.to);
    };

    const handleKeyDown = (e: React.KeyboardEvent): void => {
        if (flatResults.length === 0) {
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((index) => (index + 1) % flatResults.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((index) => (index - 1 + flatResults.length) % flatResults.length);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            handleSelect(flatResults[activeIndex], e.metaKey || e.ctrlKey)();
        }
    };

    //=======================================//
    //=============== Render ================//
    //=======================================//

    const renderHint = (): JSX.Element => {
        const { term, sourceId, navigationOnly } = parsed;
        const shortest = availableSources.length > 0 ? Math.min(...availableSources.map((s) => s.minLength)) : 0;

        if (groups.length > 0) {
            return null;
        }
        if (!term && !sourceId) {
            return <div className={classes.empty}>{t('empty_hint')}</div>;
        }
        if (search.loading) {
            return <div className={classes.empty}>{t('searching')}</div>;
        }
        if (term.length < shortest && !navigationOnly) {
            return <div className={classes.empty}>{t('min_chars', { count: shortest })}</div>;
        }
        return <div className={classes.empty}>{t('no_result', { query: term })}</div>;
    };

    // Rebuilt on every render so a shrinking result list leaves no detached node behind
    itemRefs.current = [];
    let itemIndex = -1;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            TransitionProps={{ onExited: () => setQuery('') }}
            maxWidth="sm"
            fullWidth
            aria-label={t('title')}
            classes={{ scrollPaper: classes.scrollPaper, paper: classes.paper }}
        >
            <div className={classes.searchRow} onKeyDown={handleKeyDown}>
                <SearchIcon className={classes.searchIcon} />
                <InputBase
                    autoFocus
                    fullWidth
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t('placeholder')}
                    className={classes.input}
                    inputProps={{ 'aria-label': t('placeholder') }}
                />
            </div>
            {search.loading ? (
                <LinearProgress className={classes.progress} />
            ) : (
                <div className={classes.progressPlaceholder} />
            )}

            <List className={classes.results} onKeyDown={handleKeyDown}>
                {renderHint()}
                {groups.map((group) => (
                    <div key={group.key}>
                        <div className={classes.groupLabel}>
                            {group.color && (
                                <span className={classes.groupDot} style={{ backgroundColor: group.color }} />
                            )}
                            {group.label}
                        </div>
                        {group.results.map((result) => {
                            itemIndex += 1;
                            const index = itemIndex;
                            return (
                                <ListItem
                                    key={result.key}
                                    ref={(node) => {
                                        itemRefs.current[index] = node;
                                    }}
                                    className={clsx(classes.item, index === activeIndex && classes.itemActive)}
                                    onClick={handleSelect(result, false)}
                                    onMouseEnter={() => setActiveIndex(index)}
                                >
                                    <ListItemIcon className={classes.itemIcon}>
                                        <Icon>{result.icon || group.icon}</Icon>
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={result.title}
                                        secondary={result.subtitle}
                                        classes={{ primary: classes.itemTitle, secondary: classes.itemSubtitle }}
                                    />
                                </ListItem>
                            );
                        })}
                    </div>
                ))}
            </List>

            <div className={classes.footer}>
                <span>
                    {t('hint_prefixes', {
                        prefixes: availableSources
                            .map((source) => `${formatPrefix(source.prefix)} ${t(source.labelKey)}`)
                            .join(' · '),
                    })}
                </span>
                <span>
                    <span className={classes.key}>↑</span>
                    <span className={classes.key}>↓</span> {t('hint_browse')}
                    <span className={classes.key}>↵</span> {t('hint_open')}
                    <span className={classes.key}>{getModifierLabel()} ↵</span> {t('hint_new_tab')}
                </span>
            </div>
        </Dialog>
    );
};

const CommandPaletteWithStyle = withStyles(styles)(CommandPalette);
const CommandPaletteWithTranslation = withTranslation(['command_palette', 'booking', 'reservation', 'menu', 'common'])(
    CommandPaletteWithStyle,
);

const mapStateToProps = (state) => ({
    favorites: homeSelectors.favoritesList(state),
});

export default connect(mapStateToProps)(CommandPaletteWithTranslation);
